import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App';
import './index.css';
import reportWebVitals from './reportWebVitals';

import './assets/fonts/fonts.css';
// Import only the styles the app uses (solid + regular) instead of all.min.css,
// which also references the Brands webfont (unused, but emitted + SW-precached).
import '@fortawesome/fontawesome-free/css/fontawesome.min.css';
import '@fortawesome/fontawesome-free/css/solid.min.css';
import '@fortawesome/fontawesome-free/css/regular.min.css';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { store } from './store';

// Sentry is DSN-driven: with no VITE_SENTRY_DSN set, init is skipped entirely
// so dev/preview behave exactly as before.
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const API_BASE_URL = import.meta.env.VITE_BASE_URL || 'http://localhost:3030';

const parseSampleRate = (raw: string | undefined, fallback: number): number => {
  const n = Number.parseFloat(raw ?? '');
  return Number.isFinite(n) ? n : fallback;
};

if (SENTRY_DSN) {
  // Propagate browser->API distributed-tracing headers only to our own API
  // origin (derived from VITE_BASE_URL), never to third parties.
  let apiOrigin: string = API_BASE_URL;
  try {
    apiOrigin = new URL(API_BASE_URL).origin;
  } catch {
    // Keep the raw value if VITE_BASE_URL is not a parseable absolute URL.
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Session Replay to diagnose the signup failure. Keeps Sentry's DEFAULT
      // masking ON (maskAllText / maskAllInputs) so the signup form's email
      // and password are never captured in replays.
      Sentry.replayIntegration(),
    ],
    // Default 0.1 to protect quota under heavy real traffic; errors are sent
    // regardless of this trace sample rate.
    tracesSampleRate: parseSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 0.1),
    // Error-biased, quota-safe replay: record a buffered replay when an error
    // happens (default 1.0), and record ~none of ordinary sessions (default 0).
    replaysOnErrorSampleRate: parseSampleRate(
      import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
      1.0
    ),
    replaysSessionSampleRate: parseSampleRate(
      import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
      0
    ),
    // Propagate trace headers to our own API origin only, plus localhost for dev.
    tracePropagationTargets: [apiOrigin, 'localhost'],
  });
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please refresh the page.</p>}>
      <Provider store={store}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </Provider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
